import { BellRing, ClipboardList, ReceiptText, UtensilsCrossed, Volume2, VolumeX, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'
import { getSoundEnabled, playNotificationSound, setSoundEnabled } from '../lib/notificationSound'

type TrackingOrder={id:string;status:string;paid?:number}
type TrackingResponse={orders?:TrackingOrder[];account?:{paid?:number;remaining?:number}}

export function GuestQuickActions({token}:{token:string}){
  const [sessionToken,setSessionToken]=useState(()=>token?window.sessionStorage.getItem(`goncalo_guest_session_${token}`)||'':'')
  const [open,setOpen]=useState(false)
  const [busy,setBusy]=useState('')
  const [message,setMessage]=useState('')
  const [soundEnabled,setSoundEnabledState]=useState(()=>getSoundEnabled('guest'))
  const previous=useRef('')

  useEffect(()=>{
    const sync=()=>setSessionToken(token?window.sessionStorage.getItem(`goncalo_guest_session_${token}`)||'':'')
    window.addEventListener('guest-session-change',sync)
    return()=>window.removeEventListener('guest-session-change',sync)
  },[token])

  useEffect(()=>{
    if(!token||!sessionToken)return
    let active=true
    async function check(){
      if(!getSoundEnabled('guest'))return
      const {data,error}=await supabase.functions.invoke('table-menu',{body:{action:'status',token,sessionToken}})
      if(!active||error||data?.error)return
      const tracking=data as TrackingResponse
      const snapshot=JSON.stringify({orders:(tracking.orders||[]).map(o=>[o.id,o.status]),paid:Number(tracking.account?.paid||0)})
      if(previous.current&&previous.current!==snapshot){
        const parsed=JSON.parse(previous.current) as {orders:Array<[string,string]>;paid:number}
        const oldStatus=new Map(parsed.orders)
        const becameReady=(tracking.orders||[]).some(o=>o.status==='ready'&&oldStatus.get(o.id)!=='ready')
        const paidMore=Number(tracking.account?.paid||0)>Number(parsed.paid||0)
        playNotificationSound(paidMore?'payment':becameReady?'urgent':'notice','guest')
      }
      previous.current=snapshot
    }
    void check()
    const timer=window.setInterval(()=>void check(),5000)
    return()=>{active=false;window.clearInterval(timer)}
  },[token,sessionToken])

  function scrollTo(selector:string,heading?:string){
    setOpen(false)
    let target=document.querySelector(selector) as HTMLElement|null
    if(!target&&heading){
      const headings=[...document.querySelectorAll('h2')]
      target=(headings.find(node=>node.textContent?.toLowerCase().includes(heading.toLowerCase()))?.closest('section') as HTMLElement|null)||null
    }
    target?.scrollIntoView({behavior:'smooth',block:'start'})
  }

  async function request(action:'request_waiter'|'request_bill'){
    setBusy(action);setMessage('')
    const {data,error}=await supabase.functions.invoke('table-menu',{body:{action,token,sessionToken}})
    if(error||data?.error)setMessage(data?.error||error?.message||'Não foi possível enviar a solicitação.')
    else{setMessage(data?.message||'Solicitação enviada.');playNotificationSound('success','guest');setOpen(false)}
    setBusy('')
  }

  function toggleSound(){
    const next=!soundEnabled
    setSoundEnabled(next,'guest');setSoundEnabledState(next)
    previous.current=''
    if(next)window.setTimeout(()=>playNotificationSound('success','guest'),20)
  }

  if(!sessionToken)return null

  return <>
    {message&&<div className="guest-quick-toast" role="status">{message}<button type="button" onClick={()=>setMessage('')}><X size={15}/></button></div>}
    <button type="button" className={`guest-quick-trigger ${open?'open':''}`} onClick={()=>setOpen(v=>!v)} aria-label="Atendimento e notificações"><BellRing size={23}/><span>Atendimento</span></button>
    {open&&<>
      <button type="button" className="guest-quick-backdrop" aria-label="Fechar opções" onClick={()=>setOpen(false)}/>
      <section className="guest-quick-menu" aria-label="Atendimento e notificações">
        <header><div><small>ATENDIMENTO</small><strong>O que você precisa?</strong></div><button type="button" onClick={()=>setOpen(false)}><X size={20}/></button></header>
        <div className="guest-quick-grid">
          <button type="button" onClick={()=>void request('request_waiter')} disabled={Boolean(busy)}><BellRing/><span>Chamar garçom</span></button>
          <button type="button" onClick={()=>void request('request_bill')} disabled={Boolean(busy)}><ReceiptText/><span>Pedir conta</span></button>
          <button type="button" onClick={()=>scrollTo('', 'Seus pedidos')}><ClipboardList/><span>Meus pedidos</span></button>
          <button type="button" onClick={()=>scrollTo('', 'Conta detalhada')}><ReceiptText/><span>Ver comanda</span></button>
          <button type="button" onClick={()=>scrollTo('.guest-menu')}><UtensilsCrossed/><span>Cardápio</span></button>
          <button type="button" className={soundEnabled?'sound-on':''} onClick={toggleSound}>{soundEnabled?<Volume2/>:<VolumeX/>}<span>{soundEnabled?'Som ligado':'Ligar som'}</span></button>
        </div>
        <small className="guest-quick-note">Com o som ligado, você recebe aviso sonoro quando o pedido mudar de etapa, ficar pronto ou o pagamento for confirmado.</small>
      </section>
    </>}
  </>
}
